// @ts-strict-ignore
import { Dictionary } from 'typescript-collections';
import { Line } from '@3d-scene/model/line';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionAreaUsageVO } from '@shared/vo/meta-element.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { Vector3 } from 'three';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ArticleLineHolder {
  private _cacheEnabled: boolean = false;
  private _articleLineCache: Dictionary<string, AdvancedLine> = new Dictionary<string, AdvancedLine>();
  private _articleToParentUsageId: Dictionary<string, string> = new Dictionary<string, string>();
  private _parentUsageIdToSortedContainerGuids: Dictionary<string, string[]> = new Dictionary<string, string[]>();

  constructor(private _componentUtils: ContainerService) {}

  public get cacheEnabled(): boolean {
    return this._cacheEnabled;
  }

  public set cacheEnabled(value: boolean) {
    this._cacheEnabled = value;
    if (!this._cacheEnabled) {
      this.clearArticleLineCache();
    }
  }

  public getArticleLineByAcceptingUsage(
    article: ArticleContainer,
    acceptingAreaUsage: ConnectionAreaUsageVO,
    baseLine: AdvancedLine,
    useCache: boolean = true
  ): AdvancedLine {
    if (!article) {
      return null;
    }

    if (!this._cacheEnabled || !useCache) {
      return this._componentUtils.findFigureLine(article, acceptingAreaUsage, baseLine);
    }

    let articleLine: AdvancedLine = this.getArticleLineFromCache(article.articleGUID);
    if (!articleLine) {
      articleLine = this._componentUtils.findFigureLine(article, acceptingAreaUsage, baseLine);
      if (articleLine && articleLine.length !== 0) {
        const parentGUID: string = acceptingAreaUsage.identity.articleUsageId;
        this._articleLineCache.setValue(parentGUID, baseLine);
        this.addArticleLineToCache(article.articleGUID, articleLine, parentGUID, baseLine);
      }
    }
    return articleLine;
  }

  public moveArticleLine(articleGUID: string, delta: Vector3): void {
    if (!this._cacheEnabled) {
      return;
    }
    let articleLine: AdvancedLine = this.getArticleLineFromCache(articleGUID);
    if (articleLine) {
      articleLine = articleLine.move(delta) as AdvancedLine;
      const parentGUID: string = this._articleToParentUsageId.getValue(articleGUID);
      const baseLine: AdvancedLine = this._articleLineCache.getValue(parentGUID);
      this.addArticleLineToCache(articleGUID, articleLine, parentGUID, baseLine);
    }
  }

  public clearArticleLineCacheByGUIDs(articleGUIDs: string[]): void {
    if (!articleGUIDs) {
      return;
    }
    for (const guid of articleGUIDs) {
      this.removeArticleLineFromCache(guid);
    }
  }

  public clearArticleLineCache(): void {
    this._articleLineCache.clear();
    this._articleToParentUsageId.clear();
    this._parentUsageIdToSortedContainerGuids.clear();
  }

  public getSortedContainerGuids(parentGUID: string): string[] {
    return this._parentUsageIdToSortedContainerGuids.containsKey(parentGUID) ? this._parentUsageIdToSortedContainerGuids.getValue(parentGUID) : [];
  }

  private addArticleLineToCache(articleGUID: string, articleLine: AdvancedLine, parentGUID: string, parentLine: Line): void {
    this._articleLineCache.setValue(articleGUID, articleLine);
    if (parentGUID) {
      this._articleToParentUsageId.setValue(articleGUID, parentGUID);
      this.removeFromSortedArticles(articleGUID);
      this.addToSortedArticles(articleGUID, articleLine, parentGUID, parentLine);
    }
  }

  private getArticleLineFromCache(articleGUID: string): AdvancedLine {
    return this._articleLineCache.containsKey(articleGUID) ? this._articleLineCache.getValue(articleGUID) : null;
  }

  private removeArticleLineFromCache(articleGUID: string): void {
    if (this._articleLineCache.containsKey(articleGUID)) {
      this.removeFromSortedArticles(articleGUID);
      this._articleLineCache.remove(articleGUID);
      this._articleToParentUsageId.remove(articleGUID);
      this._parentUsageIdToSortedContainerGuids.remove(articleGUID);
    }
  }

  private addToSortedArticles(articleGUID: string, articleLine: Line, parentGUID: string, baseLine: Line): void {
    let sortedContainerGuids: string[] = this._parentUsageIdToSortedContainerGuids.getValue(parentGUID);
    if (!sortedContainerGuids) {
      sortedContainerGuids = [];
      this._parentUsageIdToSortedContainerGuids.setValue(parentGUID, sortedContainerGuids);
    } else {
      for (let index: number = 0; index < sortedContainerGuids.length; index++) {
        const storedArticleGuid: string = sortedContainerGuids[index];
        const storedArticleLine: Line = this.getArticleLineFromCache(storedArticleGuid);
        if (storedArticleLine.start.distanceTo(baseLine.start) > articleLine.start.distanceTo(baseLine.start)) {
          sortedContainerGuids.splice(index, 0, articleGUID);
          return;
        }
      }
    }
    sortedContainerGuids.push(articleGUID);
  }

  private removeFromSortedArticles(articleGUID: string): void {
    const parentGUID: string = this._articleToParentUsageId.getValue(articleGUID);
    if (!parentGUID) {
      return;
    }
    const sortedContainerGuids: string[] = this._parentUsageIdToSortedContainerGuids.getValue(parentGUID);
    if (!sortedContainerGuids) {
      return;
    }
    const index: number = sortedContainerGuids.indexOf(articleGUID);
    if (index >= 0) {
      sortedContainerGuids.splice(index, 1);
    }
  }
}
